import React, { createContext, useContext, useState, useEffect } from 'react';
import { AuthContextType, User, UserPlan } from '../types';
import { supabase } from '../lib/supabase';
import { observeAuthSession } from '../lib/authSession';
import type { Session } from '@supabase/supabase-js';
import { AuthModal } from '../components/AuthModal';
import { MaintenanceScreen } from '../components/MaintenanceScreen';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Cache profile to localStorage for resilience against network failures
const PROFILE_CACHE_KEY = 'ezart_cached_profile';

const cacheProfile = (userId: string, profile: Partial<User>) => {
  try {
    const cached = { userId, profile, cachedAt: Date.now() };
    localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(cached));
  } catch (e) {
    console.warn('Failed to cache profile:', e);
  }
};

const getCachedProfile = (userId: string): Partial<User> | null => {
  try {
    const raw = localStorage.getItem(PROFILE_CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    // Only use cache if it's for the same user and less than 7 days old
    if (cached.userId === userId && Date.now() - cached.cachedAt < 7 * 24 * 60 * 60 * 1000) {
      return cached.profile;
    }
    return null;
  } catch (e) {
    return null;
  }
};

const clearCachedProfile = () => {
  try {
    localStorage.removeItem(PROFILE_CACHE_KEY);
  } catch (e) {
    // Ignore
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionRestoreError, setSessionRestoreError] = useState(false);
  const [sessionAttempt, setSessionAttempt] = useState(0);
  const [profileSyncedForUserId, setProfileSyncedForUserId] = useState<string | null>(null);
  const [profileFetchError, setProfileFetchError] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [spotifyToken, setSpotifyToken] = useState<string | null>(null);
  const [maintenanceMode, setMaintenanceMode] = useState<{ enabled: boolean; message: string } | null>(null);
  const [isAllowedUser, setIsAllowedUser] = useState(true);
  const [creditBalance, setCreditBalance] = useState(0);

  const isSupabaseConfigured = !!(import.meta as any).env.VITE_SUPABASE_URL;

  // Supabase owns session storage, URL callbacks and token refresh. Never render
  // the signed-out page because a startup timer expired or an old read finished.
  useEffect(() => {
    let mounted = true;
    setSessionRestoreError(false);
    if (!isSupabaseConfigured) {
      const storedUser = localStorage.getItem('ez_user');
      if (storedUser) setUser(JSON.parse(storedUser));
      setIsLoading(false);
      return;
    }

    const oauthError = new URLSearchParams(window.location.search).get('error')
      || new URLSearchParams(window.location.hash.slice(1)).get('error');
    if (oauthError) {
      window.dispatchEvent(new CustomEvent('app:toast', {
        detail: { message: 'Sign-in or account linking was cancelled or could not be completed.', type: 'error' }
      }));
      const cleanURL = new URL(window.location.href);
      ['error', 'error_code', 'error_description'].forEach(key => cleanURL.searchParams.delete(key));
      if (cleanURL.hash.includes('error=')) cleanURL.hash = '';
      window.history.replaceState(null, '', cleanURL.pathname + cleanURL.search + cleanURL.hash);
    }

    const mapSessionToUser = (session: Session): User => {
      const sbUser = session.user;
      const username = sbUser.user_metadata?.username;
      return {
        id: sbUser.id,
        name: sbUser.user_metadata?.full_name || username || 'Creator',
        username,
        email: sbUser.email || '',
        plan: 'free',
        avatar: sbUser.user_metadata?.avatar_url || 'https://via.placeholder.com/150'
      };
    };

    const stop = observeAuthSession(supabase.auth, session => {
      setSessionRestoreError(false);
      if (session) {
        const newUser = mapSessionToUser(session);
        setUser(prev => prev?.id === newUser.id
          ? { ...newUser, plan: prev.plan, username: prev.username || newUser.username }
          : newUser);
        // Keep provider tokens private and run database work outside the SDK's
        // synchronous auth callback, which may hold its session lock.
        if (session.provider_token?.startsWith('BQ')) {
          const providerToken = session.provider_token;
          setSpotifyToken(providerToken);
          setTimeout(() => {
            if (!mounted) return;
            void supabase.from('profiles').upsert({
              id: session.user.id, spotify_token: providerToken,
              updated_at: new Date().toISOString()
            }).then(({ error }) => {
              if (error) console.warn('Could not retain the linked Spotify account.');
            });
          }, 0);
        }
      } else {
        setUser(null);
        setSpotifyToken(null);
      }
      setIsLoading(false);
    }, () => {
      // A temporary connection failure does not mean the session was revoked.
      setSessionRestoreError(true);
    });
    return () => { mounted = false; stop(); };
  }, [isSupabaseConfigured, sessionAttempt]);

  // Check maintenance mode
  useEffect(() => {
    if (!isSupabaseConfigured) return;

    const checkMaintenanceMode = async () => {
      try {
        const { data } = await supabase
          .from('site_settings')
          .select('value')
          .eq('key', 'maintenance_mode')
          .single();

        if (data?.value) {
          const settings = data.value as { enabled: boolean; allowed_emails: string[]; message: string };
          setMaintenanceMode({ enabled: settings.enabled, message: settings.message });

          // Check if current user is allowed
          if (user?.email && settings.enabled) {
            const allowed = settings.allowed_emails?.includes(user.email) ?? false;
            setIsAllowedUser(allowed);
          } else {
            setIsAllowedUser(true); // Not logged in or maintenance off
          }
        }
      } catch (e) {
        console.warn('Failed to check maintenance mode:', e);
      }
    };

    checkMaintenanceMode();
  }, [user?.email, isSupabaseConfigured]);

  // Profile Sync (Background)
  useEffect(() => {
    let mounted = true;
    if (!user || !isSupabaseConfigured) {
      // No user - keep profileSynced null until actual user profile is loaded
      return;
    }

    const userId = user.id; // Capture for closure

    const fetchProfile = async () => {
      // SWR Pattern: Load from cache FIRST for instant UI
      const cachedProfile = getCachedProfile(userId);
      if (cachedProfile && cachedProfile.username) {
        console.log('Loading profile from cache (SWR)');
        setUser(prev => {
          if (!prev || prev.id !== userId) return prev;
          return {
            ...prev,
            plan: cachedProfile.plan || prev.plan,
            name: cachedProfile.name || prev.name,
            username: cachedProfile.username || prev.username,
            avatar: cachedProfile.avatar || prev.avatar,
            preferences: cachedProfile.preferences || prev.preferences || {}
          };
        });
        // Mark as synced immediately so OnboardingModal doesn't flash
        setProfileFetchError(false);
        setProfileSyncedForUserId(userId);
      }

      // Then revalidate from network in background
      try {
        const { data } = await supabase
          .from('profiles')
          .select('plan, full_name, username, avatar_url, spotify_token, preferences, subscription_tier, subscription_status')
          .eq('id', userId)
          .maybeSingle();

        // Fetch credit balance
        const { data: balanceData } = await supabase
          .from('credit_balances')
          .select('balance')
          .eq('user_id', userId)
          .maybeSingle();

        if (mounted && balanceData) {
          setCreditBalance(balanceData.balance || 0);
        }

        if (mounted) {
          setProfileFetchError(false);

          if (data) {
            // Load spotify token from DB if we don't have one in memory
            if (data.spotify_token && !spotifyToken && data.spotify_token.startsWith('BQ')) {
              setSpotifyToken(data.spotify_token);
            }

            // Use username as name if no full_name is set
            const profileName = data.full_name || data.username;

            const profileData = {
              plan: ((data.subscription_tier || data.plan) as UserPlan) || 'free',
              name: profileName,
              username: data.username,
              avatar: data.avatar_url,
              subscription_tier: data.subscription_tier || 'free',
              subscription_status: data.subscription_status || 'inactive',
              preferences: data.preferences || {}
            };

            // Update cache with fresh data
            if (data.username) {
              cacheProfile(userId, profileData);
            }

            setUser(prev => {
              if (!prev || prev.id !== userId) return prev;
              return {
                ...prev,
                plan: profileData.plan,
                name: profileData.name || prev.name,
                username: profileData.username || prev.username,
                avatar: profileData.avatar || prev.avatar,
                preferences: profileData.preferences
              };
            });
          }
          // Mark as synced (may already be synced from cache)
          setProfileSyncedForUserId(userId);
        }
      } catch (e) {
        console.error('Profile fetch error:', e);
        // Network failed - but if we loaded from cache above, we're fine
        if (mounted) {
          if (!cachedProfile || !cachedProfile.username) {
            // No cache was available - mark as error
            setProfileFetchError(true);
          }
          setProfileSyncedForUserId(userId);
        }
      }
    };

    fetchProfile();
    return () => { mounted = false; };
  }, [user?.id, isSupabaseConfigured]);

  // Actions
  const loginWithGoogle = async () => {
    if (isSupabaseConfigured) {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: window.location.origin }
      });
      if (error) throw error;
    } else {
      mockLogin('Google User');
    }
  };

  const loginWithSpotify = async () => {
    if (!isSupabaseConfigured || !user) {
      window.dispatchEvent(new CustomEvent('app:toast', {
        detail: { message: 'Please log in first before connecting Spotify.', type: 'error' }
      }));
      return;
    }

    try {
      // Save current route to restore after OAuth redirect
      const currentRoute = window.location.hash || '#story';
      sessionStorage.setItem('spotify_return_route', currentRoute);

      // Get the OAuth URL from our Edge Function
      const supabaseUrl = (import.meta as any).env.VITE_SUPABASE_URL;
      // Get anon key - try env first, fallback to extracting from supabase client
      let supabaseAnonKey = (import.meta as any).env.VITE_SUPABASE_ANON_KEY;
      if (!supabaseAnonKey) {
        // Fallback: get from Supabase client's internal state
        supabaseAnonKey = (supabase as any).supabaseKey || (supabase as any)._supabaseKey;
      }
      const callbackUrl = `${window.location.origin}/spotify-callback`;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/spotify-auth?action=authorize&redirect_uri=${encodeURIComponent(callbackUrl)}&user_id=${user.id}&return_to=${encodeURIComponent(currentRoute)}`,
        {
          headers: {
            'apikey': supabaseAnonKey || '',
          }
        }
      );

      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      } else if (data.error) {
        throw new Error(data.error);
      }
    } catch (error: any) {
      console.error('Spotify connect error:', error);
      window.dispatchEvent(new CustomEvent('app:toast', {
        detail: { message: `Spotify Connect Failed: ${error.message}`, type: 'error' }
      }));
    }
  };

  // Function to refresh Spotify token
  const refreshSpotifyToken = async () => {
    if (!isSupabaseConfigured || !user) return null;

    try {
      const supabaseUrl = (import.meta as any).env.VITE_SUPABASE_URL;
      const supabaseAnonKey = (import.meta as any).env.VITE_SUPABASE_ANON_KEY;
      const { data: sessionData } = await supabase.auth.getSession();

      const response = await fetch(
        `${supabaseUrl}/functions/v1/spotify-auth?action=refresh`,
        {
          method: 'POST',
          headers: {
            'apikey': supabaseAnonKey,
            'Authorization': `Bearer ${sessionData?.session?.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      if (data.access_token) {
        setSpotifyToken(data.access_token);
        return data.access_token;
      }
      return null;
    } catch (error) {
      console.error('Token refresh error:', error);
      return null;
    }
  };

  // Function to unlink Spotify
  const unlinkSpotify = async () => {
    if (!isSupabaseConfigured || !user) return;

    try {
      // Clear Spotify tokens from the profile
      await supabase.from('profiles').update({
        spotify_token: null,
        spotify_refresh_token: null,
        spotify_token_expires_at: null,
        updated_at: new Date().toISOString(),
      }).eq('id', user.id);

      // Clear local state
      setSpotifyToken(null);

      window.dispatchEvent(new CustomEvent('app:toast', {
        detail: { message: 'Spotify disconnected successfully.', type: 'success' }
      }));
    } catch (error: any) {
      console.error('Unlink Spotify error:', error);
      window.dispatchEvent(new CustomEvent('app:toast', {
        detail: { message: `Failed to disconnect Spotify: ${error.message}`, type: 'error' }
      }));
    }
  };

  const loginWithEmail = async (email: string) => {
    if (isSupabaseConfigured) {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: window.location.origin }
      });
      if (error) throw error;
    } else {
      mockLogin(email.split('@')[0]);
    }
  };

  const loginWithPassword = async (email: string, password: string) => {
    if (isSupabaseConfigured) {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
    } else {
      mockLogin(email.split('@')[0]);
    }
  };

  const generateUniqueUsername = async (): Promise<string> => {
    // Reddit-style username generator with adjectives and nouns
    const adjectives = [
      'Happy', 'Silly', 'Clever', 'Brave', 'Cosmic', 'Digital', 'Epic', 'Fancy', 'Gentle', 'Hidden',
      'Jolly', 'Quiet', 'Rapid', 'Silent', 'Wise', 'Bright', 'Cool', 'Daring', 'Electric', 'Funky',
      'Golden', 'Icy', 'Lucky', 'Mystic', 'Noble', 'Ocean', 'Purple', 'Royal', 'Stellar', 'Velvet',
      'Wild', 'Zen', 'Azure', 'Bold', 'Crimson', 'Emerald', 'Frost', 'Jade', 'Lunar', 'Neon',
      'Pixel', 'Quantum', 'Ruby', 'Solar', 'Turbo', 'Ultra', 'Vivid', 'Wonder', 'Zesty'
    ];

    const nouns = [
      'Panda', 'Tiger', 'Eagle', 'Phoenix', 'Dragon', 'Wolf', 'Fox', 'Hawk', 'Raven', 'Owl',
      'Bear', 'Lion', 'Shark', 'Whale', 'Dolphin', 'Falcon', 'Lynx', 'Otter', 'Rabbit', 'Deer',
      'Artist', 'Dreamer', 'Explorer', 'Hunter', 'Knight', 'Mage', 'Ninja', 'Pirate', 'Ranger', 'Sage',
      'Voyager', 'Wanderer', 'Wizard', 'Champion', 'Guardian', 'Hero', 'Legend', 'Master', 'Rebel', 'Spirit',
      'Shadow', 'Storm', 'Thunder', 'Blaze', 'Comet', 'Nova', 'Star', 'Moon', 'Sun', 'Sky'
    ];

    const generateCandidate = () => {
      const adj = adjectives[Math.floor(Math.random() * adjectives.length)];
      const noun = nouns[Math.floor(Math.random() * nouns.length)];
      const num = Math.floor(Math.random() * 99) + 1; // 1-99

      // 70% chance no number, 30% chance with number
      if (Math.random() > 0.3) {
        return `${adj}${noun}`;
      } else {
        return `${adj}${noun}${num}`;
      }
    };

    let attempts = 0;
    const maxAttempts = 10;

    while (attempts < maxAttempts) {
      const candidate = generateCandidate();

      // Check if username exists in database
      const { data, error } = await supabase
        .from('profiles')
        .select('username')
        .eq('username', candidate)
        .maybeSingle();

      if (error) {
        console.warn('Username check error:', error);
        attempts++;
        continue;
      }

      // If no user found with this username, it's available
      if (!data) {
        return candidate;
      }

      attempts++;
    }

    // Fallback to timestamp-based username if all attempts fail
    return `User${Date.now().toString(36)}`;
  };

  const signupWithPassword = async (email: string, password: string, fullName?: string, username?: string) => {
    if (isSupabaseConfigured) {
      // Auto-generate username if not provided
      const finalUsername = username || await generateUniqueUsername();
      // Use username as name if name not provided
      const finalName = fullName || finalUsername;

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: window.location.origin,
          data: { full_name: finalName, username: finalUsername }
        }
      });
      if (error) throw error;
      if (!data.session) throw new Error('Please check your email to confirm your account.');
    } else {
      mockLogin(fullName || 'Creator');
    }
  };

  const logout = async () => {
    if (isSupabaseConfigured) {
      const { error } = await supabase.auth.signOut();
      if (error) {
        window.dispatchEvent(new CustomEvent('app:toast', {
          detail: { message: 'Could not sign out. Please retry.', type: 'error' }
        }));
        return;
      }
      setUser(null);
      setIsLoading(false);

      setSpotifyToken(null);
      clearCachedProfile();
      // The SDK has cleared this project's session and broadcast sign-out.
    } else {
      setUser(null);
      localStorage.removeItem('ez_user');
      clearCachedProfile();
    }
  };

  const openAuthModal = () => setIsAuthModalOpen(true);
  const closeAuthModal = () => setIsAuthModalOpen(false);

  const mockLogin = (name: string) => {
    const newUser: User = {
      id: 'u_123',
      name: name,
      email: 'kai@ezart.studio',
      plan: 'free',
      avatar: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=100&q=80'
    };
    setUser(newUser);
    localStorage.setItem('ez_user', JSON.stringify(newUser));
    setIsAuthModalOpen(false);
  };

  const updateProfile = async (updates: { name?: string; username?: string; avatar?: string }) => {
    if (!user) return;

    if (isSupabaseConfigured) {
      const dbUpdates: any = {
        id: user.id,
        updated_at: new Date().toISOString(),
        email: user.email
      };
      if (updates.name) dbUpdates.full_name = updates.name;
      if (updates.username) dbUpdates.username = updates.username;
      if (updates.avatar) dbUpdates.avatar_url = updates.avatar;

      console.log('🔄 Saving profile (background)...', dbUpdates);

      // Fire and forget - don't wait for DB, just attempt save in background
      (async () => {
        try {
          const { error } = await supabase.from('profiles').upsert(dbUpdates);
          if (error) console.warn('⚠️ Profile DB save failed:', error.message);
          else console.log('✅ Profile saved to DB');
        } catch (err) {
          console.warn('⚠️ Profile save error:', err);
        }
      })();
    }

    // Update local state and cache
    const updatedUser = { ...user, ...updates };
    setUser(updatedUser);

    // Cache profile with username for resilience against network failures
    if (updatedUser.username) {
      cacheProfile(user.id, {
        plan: updatedUser.plan,
        name: updatedUser.name,
        username: updatedUser.username,
        avatar: updatedUser.avatar,
        preferences: updatedUser.preferences
      });
    }
  };

  const updatePreferences = async (prefs: Record<string, any>) => {
    if (!user) return;

    // Update local state immediately for responsiveness
    setUser(prev => {
      if (!prev) return null;
      return {
        ...prev,
        preferences: { ...prev.preferences, ...prefs }
      };
    });

    if (isSupabaseConfigured) {
      // Fetch current preferences first to merge, or use jsonb_set in SQL?
      // For simplicity, we assume generic update:
      // Note: We need a way to merge. 'preferences' jsonb column handles object merge on UPDATE? 
      // No, updating a JSON column typically replaces it unless using specific jsonb functions.
      // Better strategy: fetch current defaults from user object (which should appear fresh) and merge.

      const updatedPrefs = { ...user.preferences, ...prefs };

      (async () => {
        try {
          const { error } = await supabase.from('profiles').update({
            preferences: updatedPrefs,
            updated_at: new Date().toISOString()
          }).eq('id', user.id);

          if (error) console.warn('⚠️ Preferences DB save failed:', error.message);
        } catch (err) {
          console.warn('⚠️ Preferences save error:', err);
        }
      })();
    } else {
      // Local storage fallback
      const stored = localStorage.getItem('ez_user');
      if (stored) {
        const parsed = JSON.parse(stored);
        parsed.preferences = { ...parsed.preferences, ...prefs };
        localStorage.setItem('ez_user', JSON.stringify(parsed));
      }
    }
  };

  const upgradeToPro = async () => {
    if (!user) return;
    if (isSupabaseConfigured) {
      await supabase.from('profiles').update({ plan: 'pro' }).eq('id', user.id);
    }
    setUser({ ...user, plan: 'pro' });
  };

  const downgradeToFree = async () => {
    if (!user) return;
    if (isSupabaseConfigured) {
      await supabase.from('profiles').update({ plan: 'free' }).eq('id', user.id);
    }
    setUser({ ...user, plan: 'free' });
  };

  // --- Credit & Billing Functions ---
  const refreshCreditBalance = async () => {
    if (!user || !isSupabaseConfigured) return;
    const { data } = await supabase
      .from('credit_balances')
      .select('balance')
      .eq('user_id', user.id)
      .maybeSingle();
    if (data) setCreditBalance(data.balance || 0);
  };

  const createCheckoutSession = async (priceId: string, mode: 'subscription' | 'payment') => {
    if (!user) return;
    const { data, error } = await supabase.functions.invoke('create-checkout-session', {
      body: {
        priceId,
        mode,
        successUrl: `${window.location.origin}/settings?checkout=success`,
        cancelUrl: `${window.location.origin}/settings?checkout=cancelled`,
      },
    });
    if (error) {
      console.error('Checkout session error:', error);
      return;
    }
    if (data?.url) {
      window.location.href = data.url;
    }
  };

  // Profile is synced only if the synced user ID matches current user
  const profileSynced = !!(user && profileSyncedForUserId === user.id);

  return (
    <AuthContext.Provider value={{
      user,
      spotifyToken,
      isLoading,
      profileSynced,
      profileFetchError,
      loginWithGoogle,
      loginWithSpotify,
      unlinkSpotify,
      refreshSpotifyToken,
      loginWithEmail,
      loginWithPassword,
      signupWithPassword,
      openAuthModal,
      closeAuthModal,
      isAuthModalOpen,
      logout,
      creditBalance,
      refreshCreditBalance,
      createCheckoutSession,
      upgradeToPro,
      downgradeToFree,
      updateProfile,
      updatePreferences
    }}>
      {/* Show maintenance screen if maintenance mode is enabled and user is not allowed */}
      {maintenanceMode?.enabled && user && !isAllowedUser ? (
        <MaintenanceScreen message={maintenanceMode.message} onLogout={logout} />
      ) : (
        sessionRestoreError ? (
          <div className="min-h-screen bg-zinc-900 flex flex-col gap-4 items-center justify-center text-zinc-200" role="alert">
            <p>Could not restore your session. Retry when your connection is available.</p>
            <button className="rounded-lg border border-white/20 px-4 py-2" onClick={() => {
              setIsLoading(true);
              setSessionAttempt(value => value + 1);
            }}>Retry session</button>
          </div>
        ) : children
      )}
      <AuthModal isOpen={isAuthModalOpen} onClose={closeAuthModal} />
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
